import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiMapPin } from '../../utils/icons';
import { updateLocation } from '../../services/adminService';
import { Location, Organization } from '../../types';

// Indian States and Union Territories
const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

interface EditLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  location: Location | null;
  organizations: Organization[];
}

interface LocationFormData {
  orgId: number;
  locationNickname: string;
  country: string;
  state: string;
  city: string;
  district: string;
  zone: string;
  suburb: string;
  colony: string;
  street: string;
  plotNo: string;
  sector: string;
  chowk: string;
  landmark: string;
  pinCode: string;
  buildingName: string;
  buildingWing: string;
  floor: string;
  officeUnit: string;
  latitude: string;
  longitude: string;
  area: string;
  locationType: string;
  status: 'approved' | 'pending' | 'inactive';
}

export const EditLocationModal: React.FC<EditLocationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  location,
  organizations,
}) => {
  const [formData, setFormData] = useState<LocationFormData>({
    orgId: location?.orgId || 0,
    locationNickname: '',
    country: 'India',
    state: '',
    city: '',
    district: '',
    zone: '',
    suburb: '',
    colony: '',
    street: '',
    plotNo: '',
    sector: '',
    chowk: '',
    landmark: '',
    pinCode: '',
    buildingName: '',
    buildingWing: '',
    floor: '',
    officeUnit: '',
    latitude: '',
    longitude: '',
    area: '',
    locationType: '',
    status: 'approved',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form with location data when location prop changes
  useEffect(() => {
    if (location && isOpen) {
      setFormData({
        orgId: location.orgId || 0,
        locationNickname: location.locationNickname || '',
        country: location.country || 'India',
        state: location.state || '',
        city: location.city || '',
        district: location.district || '',
        zone: location.zone || '',
        suburb: location.suburb || '',
        colony: location.colony || '',
        street: location.street || '',
        plotNo: location.plotNo || '',
        sector: location.sector || '',
        chowk: '',
        landmark: location.landmark || '',
        pinCode: location.pinCode || '',
        buildingName: location.buildingName || '',
        buildingWing: location.buildingWing || '',
        floor: location.floor || '',
        officeUnit: location.officeUnit || '',
        latitude: location.latitude?.toString() || '',
        longitude: location.longitude?.toString() || '',
        area: location.area?.toString() || '',
        locationType: location.locationType || '',
        status: (location.status as 'approved' | 'pending' | 'inactive') || 'approved',
      });
    }
  }, [location, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.orgId) {
      return 'Please select an organization';
    }
    if (!formData.locationNickname.trim()) {
      return 'Location nickname is required';
    }
    if (!formData.latitude || !formData.longitude) {
      return 'Latitude and longitude are required';
    }
    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      return 'Latitude must be between -90 and 90';
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      return 'Longitude must be between -180 and 180';
    }
    if (formData.pinCode && !/^[0-9]{6}$/.test(formData.pinCode)) {
      return 'Pin code must be exactly 6 digits';
    }
    if (formData.area && (isNaN(parseFloat(formData.area)) || parseFloat(formData.area) <= 0)) {
      return 'Area must be a positive number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!location || !location.locationId) {
      setError('Location data is missing');
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        orgId: formData.orgId,
        locationNickname: formData.locationNickname.trim(),
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        status: formData.status,
      };

      // Add optional fields only if they have values
      if (formData.country.trim()) payload.country = formData.country.trim();
      if (formData.state.trim()) payload.state = formData.state.trim();
      if (formData.city.trim()) payload.city = formData.city.trim();
      if (formData.district.trim()) payload.district = formData.district.trim();
      if (formData.zone.trim()) payload.zone = formData.zone.trim();
      if (formData.suburb.trim()) payload.suburb = formData.suburb.trim();
      if (formData.colony.trim()) payload.colony = formData.colony.trim();
      if (formData.street.trim()) payload.street = formData.street.trim();
      if (formData.plotNo.trim()) payload.plotNo = formData.plotNo.trim();
      if (formData.sector.trim()) payload.sector = formData.sector.trim();
      if (formData.chowk.trim()) payload.chowk = formData.chowk.trim();
      if (formData.landmark.trim()) payload.landmark = formData.landmark.trim();
      if (formData.pinCode.trim()) payload.pinCode = formData.pinCode.trim();
      if (formData.buildingName.trim()) payload.buildingName = formData.buildingName.trim();
      if (formData.buildingWing.trim()) payload.buildingWing = formData.buildingWing.trim();
      if (formData.floor.trim()) payload.floor = formData.floor.trim();
      if (formData.officeUnit.trim()) payload.officeUnit = formData.officeUnit.trim();
      if (formData.area.trim()) payload.area = parseFloat(formData.area);
      if (formData.locationType.trim()) payload.locationType = formData.locationType.trim();

      const response = await updateLocation(location.locationId, payload);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError('Failed to update location');
      }
    } catch (err: any) {
      console.error('Error updating location:', err);
      setError(err.message || 'An error occurred while updating the location');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FiMapPin />
            Edit Location
          </ModalTitle>
          <CloseButton onClick={handleClose} disabled={loading}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Section>
            <SectionTitle>Basic Information</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>
                  Organization <Required>*</Required>
                </Label>
                <Select
                  name="orgId"
                  value={formData.orgId}
                  onChange={(e) => setFormData(prev => ({ ...prev, orgId: parseInt(e.target.value) || 0 }))}
                  disabled={loading}
                >
                  <option value={0}>Select an organization</option>
                  {organizations.map((org) => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>
                  Location Nickname <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  name="locationNickname"
                  value={formData.locationNickname}
                  onChange={handleInputChange}
                  placeholder="Enter location nickname"
                  disabled={loading}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Location Type</Label>
                <Input
                  type="text"
                  name="locationType"
                  value={formData.locationType}
                  onChange={handleInputChange}
                  placeholder="e.g., Office, Warehouse, Store"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Area (sq ft)</Label>
                <Input
                  type="number"
                  step="0.01"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  placeholder="Enter area"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>
          </Section>

          <Section>
            <SectionTitle>Address Details</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>Country</Label>
                <Input
                  type="text"
                  name="country"
                  value={formData.country}
                  disabled
                  readOnly
                />
              </FormGroup>
              <FormGroup>
                <Label>State</Label>
                <Select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">Select a state</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>City</Label>
                <Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>District</Label>
                <Input
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  placeholder="Enter district"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Zone</Label>
                <Input
                  type="text"
                  name="zone"
                  value={formData.zone}
                  onChange={handleInputChange}
                  placeholder="Enter zone"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Suburb</Label>
                <Input
                  type="text"
                  name="suburb"
                  value={formData.suburb}
                  onChange={handleInputChange}
                  placeholder="Enter suburb"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Colony</Label>
                <Input
                  type="text"
                  name="colony"
                  value={formData.colony}
                  onChange={handleInputChange}
                  placeholder="Enter colony"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Street</Label>
                <Input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="Enter street"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Plot No</Label>
                <Input
                  type="text"
                  name="plotNo"
                  value={formData.plotNo}
                  onChange={handleInputChange}
                  placeholder="Enter plot number"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Sector</Label>
                <Input
                  type="text"
                  name="sector"
                  value={formData.sector}
                  onChange={handleInputChange}
                  placeholder="Enter sector"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Chowk</Label>
                <Input
                  type="text"
                  name="chowk"
                  value={formData.chowk}
                  onChange={handleInputChange}
                  placeholder="Enter chowk"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Landmark</Label>
                <Input
                  type="text"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  placeholder="Enter landmark"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Pin Code</Label>
                <Input
                  type="text"
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit pin code"
                  maxLength={6}
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>
          </Section>

          <Section>
            <SectionTitle>Building Details</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>Building Name</Label>
                <Input
                  type="text"
                  name="buildingName"
                  value={formData.buildingName}
                  onChange={handleInputChange}
                  placeholder="Enter building name"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Building Wing</Label>
                <Input
                  type="text"
                  name="buildingWing"
                  value={formData.buildingWing}
                  onChange={handleInputChange}
                  placeholder="e.g., A, B, East"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Floor</Label>
                <Input
                  type="text"
                  name="floor"
                  value={formData.floor}
                  onChange={handleInputChange}
                  placeholder="Enter floor"
                  disabled={loading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Office Unit</Label>
                <Input
                  type="text"
                  name="officeUnit"
                  value={formData.officeUnit}
                  onChange={handleInputChange}
                  placeholder="Enter office unit"
                  disabled={loading}
                />
              </FormGroup>
            </FormRow>
          </Section>

          <Section>
            <SectionTitle>Coordinates</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>
                  Latitude <Required>*</Required>
                </Label>
                <Input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  placeholder="e.g., 28.7041"
                  disabled={loading}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  Longitude <Required>*</Required>
                </Label>
                <Input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  placeholder="e.g., 77.1025"
                  disabled={loading}
                  required
                />
              </FormGroup>
            </FormRow>
          </Section>

          <Section>
            <SectionTitle>Status</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>Status</Label>
                <Select name="status" value={formData.status} onChange={handleInputChange} disabled={loading}>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </FormGroup>
            </FormRow>
          </Section>

          <ButtonGroup>
            <CancelButton type="button" onClick={handleClose} disabled={loading}>
              Cancel
            </CancelButton>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.divider};
    border-radius: 4px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.background};
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Form = styled.form`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.error};
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Select = styled.select`
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.2s;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.error}15;
  border: 1px solid ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.error};
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CancelButton = styled(Button)`
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.divider};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.divider};
  }
`;

const SubmitButton = styled(Button)`
  background: ${({ theme }) => theme.colors.primary};
  color: white;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}40;
  }
`;
